import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";
import { MdPublicOff } from "react-icons/md";

import { DIALOG_BACKDROP_SX, dialogPaperSurface } from "@/components/Core/Dialogs/surface";
import { getCountryOptions } from "@/constants/countries";
import { GEO_STATUS_QUERY_KEY } from "@/constants/queryKeys";
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from "@/constants/support";
import { copyText, useSupportChatEnabled, useSupportWidgetConfig, useTranslate } from "@/hooks";
import { openSupport } from "@/lib/supportWidget";
import { getGeoStatus } from "@/services";

// Above the site's toasts and dialogs: nothing may cover this notice.
const TOP_LAYER = 100000;

/**
 * Covers the site for a visitor from a blocked country or IP, and cannot be closed.
 * The server's gates stay the real block; this only reads /auth/geo, never Fingerprint.
 */
export default function GeoBlockNotice() {
  const t = useTranslate();
  const theme = useTheme();
  const { i18n } = useTranslation();
  const { isPending: chatPending } = useSupportWidgetConfig();
  const chat = useSupportChatEnabled();
  const [copied, setCopied] = useState(false);

  // A failed check shows nothing: the gates on the server still apply.
  const { data: geo } = useQuery({
    queryKey: GEO_STATUS_QUERY_KEY,
    queryFn: getGeoStatus,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  const shown = Boolean(geo?.blocked || geo?.restricted);
  const code = shown ? geo?.country : null;
  const countryName = useMemo(
    () => (code ? getCountryOptions(i18n.language).find(({ value }) => value === code)?.label : ""),
    [code, i18n.language],
  );

  if (!shown) return null;

  // An IP block is about the connection, so it names no country.
  const byIp = Boolean(geo?.blocked);
  const named = !byIp && Boolean(code && countryName);
  const warning = theme.palette.warning.main;

  return (
    <Dialog
      open
      disableEscapeKeyDown
      // The support chat lives outside this dialog and needs the keyboard.
      disableEnforceFocus
      sx={{ zIndex: TOP_LAYER }}
      PaperProps={{
        sx: {
          ...dialogPaperSurface(theme),
          // Narrower than the shared minimum allows, so it fits a small phone.
          minWidth: 0,
          width: "calc(100% - 32px)",
          maxWidth: 400,
          m: 2,
        },
      }}
      slotProps={{ backdrop: { sx: DIALOG_BACKDROP_SX } }}
      aria-labelledby="geo-block-title"
      aria-describedby="geo-block-desc"
    >
      <Stack alignItems="center" textAlign="center" sx={{ px: { xs: 2.5, sm: 4 }, py: 4 }}>
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            width: 72,
            height: 72,
            borderRadius: "50%",
            color: warning,
            backgroundColor: alpha(warning, 0.12),
            boxShadow: `inset 0 0 0 1px ${alpha(warning, 0.3)}`,
          }}
        >
          <MdPublicOff size={36} aria-hidden />
        </Box>

        <Typography
          id="geo-block-title"
          component="h2"
          variant="h5"
          sx={{ mt: 2.5, fontWeight: 800, overflowWrap: "anywhere" }}
        >
          {t(byIp ? "geo-block.title-ip" : "geo-block.title")}
        </Typography>

        <Typography id="geo-block-desc" color="text.secondary" sx={{ mt: 1 }}>
          {t(byIp ? "geo-block.desc-ip" : "geo-block.desc-country")}
        </Typography>

        {named && (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              mt: 2,
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <ReactCountryFlag
              countryCode={code as string}
              svg
              aria-hidden
              style={{ width: 20, height: 15, objectFit: "cover", borderRadius: 2 }}
            />
            <Typography variant="caption" color="text.secondary">
              {t("geo-block.detected", { country: countryName })}
            </Typography>
          </Stack>
        )}

        {chat || chatPending ? (
          <Button
            variant="contained"
            size="large"
            fullWidth
            // Until the config arrives, openSupport() would take the mail route.
            disabled={chatPending}
            onClick={openSupport}
            sx={{ mt: 3, borderRadius: 999 }}
          >
            {t("help-center.contact-chat")}
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              size="large"
              fullWidth
              component="a"
              href={SUPPORT_MAILTO}
              onClick={async () => setCopied(await copyText(SUPPORT_EMAIL))}
              sx={{ mt: 3, borderRadius: 999 }}
            >
              {t("help-center.contact-email")}
            </Button>
            {/* Shown here, not as a toast: toasts sit under this notice, and mailto does nothing without a mail app. */}
            <Typography
              role="status"
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1.5, overflowWrap: "anywhere" }}
            >
              {t(copied ? "help-center.email-copied" : "help-center.email-us", {
                email: SUPPORT_EMAIL,
              })}
            </Typography>
          </>
        )}
      </Stack>
    </Dialog>
  );
}
